import {
    ActionContext,
    ActionTree,
    GetterTree,
    MutationTree,
    Module,
    Store as VuexStore,
    CommitOptions,
    DispatchOptions,
} from 'vuex'

import { State as RootState } from '@/store'

import {User, WalletItem} from "@/types/UserType";
import {login, logout, register, socialProviderCallback} from "@/data/requests/auth";
import {fetchUser, fetchWallet, updateAvatar} from "@/data/requests/user";
import {reset, sendReset} from "@/data/requests/password";
import {resendVerification, verifyVerification} from "@/data/requests/verification";
import {client, uploaderClient} from "@/data/axios";

// Declare state
export type State = {
    token: string | null,
    user: User | null,
    language: string | null,
    wallet: WalletItem[],
}

// Mutation contracts
export type Mutations<S = State> = {
    setUser(state: S, { user }: { user: User }): void,
    setToken(state: S, { token }: { token: string | null }): void,
    setWallet(state: S, { wallet }: { wallet: WalletItem[] }): void,
    setUserAvatar(state: S, { avatar }: { avatar: string }): void,
    addStorageUsed(state: S, { bytes }: { bytes: number }): void
}

// Actions context
type AugmentedActionContext = {
    commit<K extends keyof Mutations>(
        key: K,
        payload: Parameters<Mutations[K]>[1],
    ): ReturnType<Mutations[K]>
} & Omit<ActionContext<State, RootState>, 'commit'>

// Actions contracts
export interface Actions {
    login(
        { commit }: AugmentedActionContext,
        payload: {email: string, password: string}
    ): Promise<any>
    register(
        { commit }: AugmentedActionContext,
        payload: {email: string, password: string, name: string},
    ): Promise<any>,
    logout(
        { commit }: AugmentedActionContext,
        payload: {}
    ): Promise<any>,
    fetchUser(
        { commit }: AugmentedActionContext,
        payload: {token: string},
    ): Promise<any>,
    fetchWallet(
        { commit }: AugmentedActionContext,
        payload: {},
    ): Promise<any>,
    uploadAvatar(
        { commit }: AugmentedActionContext,
        payload: { file: File }
    ): Promise<any>,
    sendResetPassword(
        { commit }: AugmentedActionContext,
        payload: {email: string}
    ): Promise<any>,
    resetPassword(
        { commit }: AugmentedActionContext,
        payload: {token: string, email: string, password: string}
    ): Promise<any>,
    resendVerification(
        { commit }: AugmentedActionContext,
        payload: {}
    ): Promise<any>,
    verifyVerification(
        { commit }: AugmentedActionContext,
        payload: {id: string, hash: string, query: {}}
    ): Promise<any>
}

// Getters types
export type Getters = {
    isLoggedIn(state: State): boolean,
    user(state: State): User | null,
    token(state: State): string | null,
    wallet(state: State): WalletItem[]
}

const state: State = {
    token: localStorage.getItem('token') || '' as string | null,
    user: null as User | null,

    language: localStorage.getItem('language') || 'uk',

    wallet: [],
};

const getters: GetterTree<State, RootState> & Getters = {
    isLoggedIn: (state) => {
        return state.token != null && state.token != '';
    },

    user: (state) => {
        return state.user;
    },

    token: (state) => {
        return state.token;
    },

    wallet(state): WalletItem[] {
        return state.wallet;
    }
};

const mutations: MutationTree<State> & Mutations = {
    setUser(state, { user }) {
        state.user = user;
    },
    setToken(state, { token }) {
        if(token == null) {
            state.token = '';

            localStorage.removeItem('token');

            return;
        }

        state.token = token;

        localStorage.setItem('token', token);
    },
    setWallet(state, {wallet}) {
        state.wallet = wallet;
    },
    setUserAvatar(state, {avatar}) {
        if(state.user) {
            state.user.avatar_url = avatar;
        }
    },
    addStorageUsed(state, {bytes}) {
        if(state.user) {
            state.user.storage.used = Math.floor(state.user.storage.used) + Math.floor(bytes);
        }
    }
};

const actions: ActionTree<State, RootState> & Actions = {
    login({commit}, {email, password}) {
        return new Promise((resolve: any, reject) => {
            login(email, password, (token: string, user: User) => {
                commit('setToken', {token});

                commit('setUser', {user});

                client.defaults.headers.common['Authorization'] = 'Bearer ' + token;
                uploaderClient.defaults.headers.common['Authorization'] = 'Bearer ' + token;

                resolve();
            }, (type, data) => {
                reject({type, data});
            });
        });
    },

    register({commit}, {email, password, name}) {
        return new Promise((resolve: any, reject) => {
            register(email, password, name, (token: string, user: User) => {
                commit("setToken", {token});

                commit("setUser", {user});

                client.defaults.headers.common['Authorization'] = 'Bearer ' + token;

                resolve();
            }, (type, data) => {
                reject({type, data});
            });
        })
    },

    loginBySocial({commit}, {provider, query}) {
        return new Promise((resolve: any, reject) => {
            socialProviderCallback(provider, query, (token: string, user: User) => {
                commit("setToken", {token});

                commit("setUser", {user});

                client.defaults.headers.common['Authorization'] = 'Bearer ' + token;

                resolve();
            }, (type, data) => {
                reject({type, data});
            });
        })
    },

    logout({commit}): Promise<any> {
        return new Promise((resolve: any, reject) => {
            logout(() => {
                commit("setToken", {token: null});

                client.defaults.headers.common['Authorization'] = '';

                resolve();
            }, (type, data) => {
                reject({type, data});
            });
        });
    },

    fetchUser({commit}, { token }) {
        return new Promise((resolve: any, reject) => {
            fetchUser((user) => {
                commit('setUser', {user});

                resolve(user);
            }, (type, data) => {
                reject({type, data});
            });
        })
    },

    fetchWallet({commit}, payload) {
        return new Promise((resolve: any, reject) => {
            fetchWallet((wallet) => {
                commit('setWallet', {wallet});

                resolve(wallet);
            }, (type, data) => {
                reject({type, data});
            });
        })
    },

    uploadAvatar({commit, state}, { file }) {
        return new Promise((resolve: any, reject) => {
            updateAvatar(file, ({data}) => {
                commit('setUserAvatar', { avatar: data.avatar });

                resolve(data.avatar);
            }, (type, data) => {
                reject({type, data});
            });
        })
    },

    sendResetPassword({commit}, {email}) {
        return new Promise((resolve: any, reject) => {
            sendReset(email, resolve, (type, data) => {
                reject({type, data});
            });
        });
    },

    resetPassword({commit}, {token, email, password}) {
        return new Promise((resolve: any, reject) => {
            reset(token, email, password, resolve, (type, data) => {
                reject({type, data});
            });
        });
    },

    resendVerification() {
        return new Promise((resolve: any, reject) => {
            resendVerification(resolve, (type, data) => {
                reject({type, data});
            });
        });
    },

    verifyVerification({commit}, {id, hash, query}) {
        return new Promise((resolve: any, reject) => {
            verifyVerification(id, hash, query, resolve, (type, data) => {
                reject({type, data});
            });
        });
    }
};

//setup store type
export type Store<S = State> = Omit<
    VuexStore<S>,
    'commit' | 'getters' | 'dispatch'
    > & {
    commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
        key: K,
        payload: P,
        options?: CommitOptions,
    ): ReturnType<Mutations[K]>
} & {
    getters: {
        [K in keyof Getters]: ReturnType<Getters[K]>
    }
} & {
    dispatch<K extends keyof Actions>(
        key: K,
        payload: Parameters<Actions[K]>[1],
        options?: DispatchOptions,
    ): ReturnType<Actions[K]>
}

export const UserModule: Module<State, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}